import { CalendarDaysIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  forwardRef,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button } from '@/components/ui/daisy/button';
import { Divider } from '@/components/ui/daisy/divider';
import { Dropdown } from '@/components/ui/daisy/dropdown';
import { useDropdownAutoAlign } from '@/components/ui/daisy/dropdown/useDropdownAutoAlign';
import { useDropdownClickOutside } from '@/components/ui/daisy/dropdown/useDropdownClickOutside';
import Fieldset from '@/components/ui/daisy/fieldset';
import { Colors, Sizes } from '@/components/ui/types';

import { DiscreteDateInput } from './DiscreteDateInput';
import { GeneralDatePicker } from './GeneralDatePicker';
import { ComplexDateInputProps } from './types';
import { useComplexDateValue } from './useComplexDateValue';
import { useDiscreteLabels } from './useDiscreteLabels';
import { useFormatDisplayValue } from './useFormatDisplayValue';

const sizeClasses: Record<Sizes, string> = {
  xs: 'input-xs',
  sm: 'input-sm',
  md: 'input-md',
  lg: 'input-lg',
  xl: 'input-xl',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'input-neutral',
  primary: 'input-primary',
  secondary: 'input-secondary',
  accent: 'input-accent',
  info: 'input-info',
  success: 'input-success',
  warning: 'input-warning',
  error: 'input-error',
};

const blurActiveElement = () => {
  const htmlActiveElement = document.activeElement as HTMLElement | null;
  htmlActiveElement?.blur();
};

const ComplexDateInputBase = forwardRef<HTMLDivElement, ComplexDateInputProps>(
  (props, ref) => {
    const { t } = useTranslation('translation', {
      keyPrefix: 'common.complexDateInput',
    });
    const defaultDiscreteLabels = useDiscreteLabels();

    const {
      value,
      onChange,
      placeholder = t('placeholder'),
      disabled = false,
      clearable = true,
      error = false,
      size = 'md',
      color = 'default',
      forceOpen = false,
      closeLabel = t('close'),
      dividerLabel = t('divider'),
      clearAriaLabel = t('clear'),
      discreteLabels = defaultDiscreteLabels,
      classNames,
    } = props;

    const formatValue = useFormatDisplayValue(discreteLabels);
    const panelRef = useRef<HTMLDivElement>(null);
    const { align, handleFocus } = useDropdownAutoAlign(panelRef);
    const [isOpen, setIsOpen] = useState(forceOpen);

    useEffect(() => {
      setIsOpen(forceOpen);
    }, [forceOpen]);

    useDropdownClickOutside({
      panelRef,
      active: isOpen && !forceOpen,
      onClickOutside: useCallback(() => setIsOpen(false), []),
    });

    const {
      internalState,
      dateRange,
      handleDiscreteChange,
      handlePickerChange,
      handleClear,
    } = useComplexDateValue(value, onChange);

    const displayValue = formatValue(value);
    const hasValue = !!displayValue;

    const openDropdown = () => {
      if (disabled) return;
      setIsOpen(true);
      handleFocus();
    };

    const closeDropdown = () => {
      setIsOpen(false);
      blurActiveElement();
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') closeDropdown();
    };

    return (
      <Dropdown
        ref={ref}
        open={isOpen}
        align={align}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        className={twMerge('w-full min-w-0', classNames?.base)}
      >
        <Dropdown.Trigger
          disabled={disabled}
          className="relative"
          onMouseDown={openDropdown}
          onClick={openDropdown}
        >
          <div
            className={twMerge(
              'input w-full cursor-pointer flex items-center gap-2',
              sizeClasses[size],
              colorClasses[color],
              error && 'input-error',
              disabled && 'input-disabled pointer-events-none',
              classNames?.input,
            )}
          >
            <CalendarDaysIcon className="size-4 shrink-0 opacity-50" />
            <ConditionalRenderer
              condition={hasValue}
              fallback={
                <span className="min-w-0 flex-1 truncate text-base-content/50">
                  {placeholder}
                </span>
              }
            >
              <span className="min-w-0 flex-1 truncate">{displayValue}</span>
            </ConditionalRenderer>
            <ConditionalRenderer condition={clearable && hasValue && !disabled}>
              <Button
                aria-label={clearAriaLabel}
                className="shrink-0"
                variant="ghost"
                size="xs"
                shape="circle"
                type="button"
                tabIndex={-1}
                onMouseDown={event => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleClear();
                }}
              >
                <XMarkIcon className="size-4 text-error" />
              </Button>
            </ConditionalRenderer>
          </div>
        </Dropdown.Trigger>

        <Dropdown.Content
          ref={panelRef}
          className={twMerge(
            'z-10 w-max rounded-box bg-base-100 p-2 shadow-sm mt-2 border border-base-300',
            classNames?.panel,
          )}
        >
          <div className="flex flex-col gap-2">
            <DiscreteDateInput
              value={internalState?.discrete ?? null}
              onChange={handleDiscreteChange}
              disabled={disabled}
              labels={discreteLabels}
            />
            <Divider className="my-0 mt-4 text-xs">{dividerLabel}</Divider>
            <GeneralDatePicker
              value={dateRange}
              onChange={handlePickerChange}
              disabled={disabled}
            />
            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                variant="soft"
                onClick={closeDropdown}
              >
                {closeLabel}
              </Button>
            </div>
          </div>
        </Dropdown.Content>
      </Dropdown>
    );
  },
);

ComplexDateInputBase.displayName = 'ComplexDateInput';

export const ComplexDateInput = Object.assign(ComplexDateInputBase, {
  DiscreteDateInput,
  GeneralDatePicker,
  Fieldset,
});
